import { memo, useCallback, useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  FileText,
  Plus,
  Warning,
  Trash,
  ArrowClockwise,
  DownloadSimple,
  CheckCircle,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "components/ui/tabs";
import { FilterBar } from "shared/components/filterBar";
import { UniversalTable } from "shared/components/UniversalTable";
import { TablePagination } from "components/TablePagination";
import { useRenovationDocuments } from "./hooks/useRenovationDocuments";
import { useRenovationDocumentsData } from "./hooks/useRenovationDocumentsData";
import { useRenovationDocumentsStats } from "./hooks/useRenovationDocumentsStats";
import { useInvestmentBudgetTotals } from "./hooks/useInvestmentBudgetTotals";
import { RenovationDocumentsStats } from "./components/RenovationDocumentsStats";
import { BuildingsWithPendingInvoices } from "./components/BuildingsWithPendingInvoices";
import { ImportReconciliationCards } from "./components/ImportReconciliationCards";
import { LedgerAccountBreakdown } from "./components/LedgerAccountBreakdown";
import { ReconciliationDebug } from "./components/ReconciliationDebug";
import { UnassignedInvoices } from "./components/UnassignedInvoices";
import {
  useImportReconciliation,
  type ImportReconciliation,
} from "./hooks/useImportReconciliation";
import { createDocumentCells } from "./shared/documentCellFactory";
import {
  LinkParentDocumentModal,
  BESTELBON_KOPPELING,
  OFFERTE_KOPPELING,
} from "./components/LinkParentDocumentModal";
import { DocumentsScopeBanner } from "./components/DocumentsScopeBanner";
import { LinkRenoTicketModal } from "./components/LinkRenoTicketModal";
import { DOCUMENTS_TABLE_CONFIG } from "./configs/documentsTableConfig";
import { useUsers } from "shared/contexts/users.context";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import ButtonNew from "components/buttonNew/buttonNew";
import { RenovationInvoiceImportModal } from "components/renovationInvoices/renovationInvoiceImportModal";
import {
  RenovationProject,
  RenovationDocument,
  DocumentApprovalStatus,
} from "shared/types/renovation";
import { DocumentUploadModal } from "screens/buildings/components/renovationTab/modals/documentUploadModal";
import { BuildingDTO } from "shared/types/building";
import UniversalPickerModal from "shared/components/UniversalPickerModal/UniversalPickerModal";
import { format } from "date-fns";

// Key under which the active tab is remembered for the current browser session.
const ACTIVE_TAB_STORAGE_KEY = "renovation-documents-active-tab";

interface RenovationDocumentsListProps {
  buildingId?: string;
  /**
   * Embedded mode — used when this component is mounted inside another page
   * (e.g. as a section of the project detail page).  Hides the page-level
   * chrome (title, header actions like "Importeer facturen" / "Document
   * uploaden") and skips URL-state handling so the host page keeps control of
   * the route.
   */
  embedded?: boolean;
  /**
   * Force the project filter to a fixed value.  Hides the "Project" filter
   * chip so the user cannot escape the parent-imposed scope.
   */
  lockedProjectId?: number;
  /**
   * Force the project part filter to a fixed value (used by the part detail
   * page).  Hides the "Project Part" filter chip.
   */
  lockedProjectPartId?: number;
}

/**
 * RenovationDocumentsList - List view for Renovation Documents
 *
 * Displays renovation documents (offers, invoices, contracts) with filtering,
 * grouping, and page-based pagination. Can be filtered by building, project,
 * document type, and status.
 *
 * Features:
 * - UniversalTable with Odoo-style page-based pagination (TablePagination)
 * - FilterBar with rightSlot for the pagination control
 * - URL-synchronized filters
 * - Server-side statistics via useRenovationDocumentsStats
 * - RenovationDocumentsStats block (total + filtered)
 */
const RenovationDocumentsListComponent = (
  props: RenovationDocumentsListProps,
) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { users } = useUsers();
  const { session } = useSession();

  // State for invoice import modal
  const [isInvoiceImportModalOpen, setIsInvoiceImportModalOpen] =
    useState(false);
  const [renovationInvoices, setRenovationInvoices] = useState<any[]>([]);
  const [availableProjects, setAvailableProjects] = useState<
    RenovationProject[]
  >([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);

  // State for document upload modal
  const [isDocumentUploadModalOpen, setIsDocumentUploadModalOpen] =
    useState(false);
  const [currentBuilding, setCurrentBuilding] = useState<BuildingDTO | null>(
    null,
  );

  // State for status picker modal
  const [isStatusPickerOpen, setIsStatusPickerOpen] = useState(false);
  const [statusPickerDocument, setStatusPickerDocument] =
    useState<RenovationDocument | null>(null);

  // State for date picker modal
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [datePickerDocument, setDatePickerDocument] =
    useState<RenovationDocument | null>(null);

  // Tab state — when the user lands with a filter already applied (deep link
  // from /renovation DataCards, project cards, etc.) start on the documents
  // tab so they immediately see the filtered results. Otherwise default to
  // "Te importeren facturen" which is the new primary entry point.
  const hasDeepLinkFilter =
    !!searchParams.get("building") ||
    !!searchParams.get("buildingId") ||
    !!searchParams.get("projectId") ||
    !!searchParams.get("projectPartId") ||
    !!searchParams.get("documentType") ||
    !!searchParams.get("documentStatus");
  // Remember the active tab across page reloads (sessionStorage) so a refresh
  // stays on the current tab instead of always snapping back to "te-importeren".
  // A deep link with filters still wins (it implies the documents tab).
  const [activeTab, setActiveTab] = useState<"te-importeren" | "geimporteerd">(
    () => {
      if (hasDeepLinkFilter) return "geimporteerd";
      try {
        const saved = sessionStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
        if (saved === "geimporteerd" || saved === "te-importeren") return saved;
      } catch {
        /* sessionStorage unavailable — fall through to default */
      }
      return "te-importeren";
    },
  );

  // Total number of invoices still to import (summed across all buildings),
  // used for the "Te importeren facturen" tab badge.
  const [pendingInvoiceCount, setPendingInvoiceCount] = useState<number>(0);

  // Coordinates a SINGLE "Vernieuwen" across all import sections. Bumping the
  // token tells the per-building list, the zonder-kostenplaats list and the
  // KPIs to refetch their (cached) data. isRefreshingAnalytics is true while a
  // full Odoo rebuild is running.
  const [analyticsRefreshToken, setAnalyticsRefreshToken] = useState(0);
  const [isRefreshingAnalytics, setIsRefreshingAnalytics] = useState(false);

  // State for delete confirmation
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteTargetDocument, setDeleteTargetDocument] =
    useState<RenovationDocument | null>(null);
  const [deletingDocumentIds, setDeletingDocumentIds] = useState<Set<number>>(
    new Set(),
  );
  /** Factuur waarvoor de bestelbon-kiezer open staat; null = gesloten. */
  const [purchaseOrderDocument, setPurchaseOrderDocument] = useState<any | null>(null);
  /** Factuur waarvoor de offerte-kiezer open staat; null = gesloten. */
  const [quoteDocument, setQuoteDocument] = useState<any | null>(null);
  /** Document waarvoor de renovatieticket-kiezer open staat; null = gesloten. */
  const [renoTicketDocument, setRenoTicketDocument] = useState<any | null>(null);

  // Bulk selection + bulk delete (used to remove a batch of over-imported
  // invoices so they can be re-imported with the correct, distribution-aware
  // amount).
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Manual "Vernieuwen betaalstatus" — force the Odoo payment-status sync.
  const [isSyncingPayments, setIsSyncingPayments] = useState(false);

  // Extract building ID and filter params.  In embedded mode the URL is
  // controlled by the host page, so we only read from props.
  const isEmbedded = !!props.embedded;
  const buildingId = isEmbedded
    ? props.buildingId
    : searchParams.get("building") ||
      searchParams.get("buildingId") ||
      props.buildingId;
  const projectId = isEmbedded
    ? props.lockedProjectId?.toString() ?? null
    : searchParams.get("projectId");
  const projectPartId = isEmbedded ? null : searchParams.get("projectPartId");
  const documentType = isEmbedded ? null : searchParams.get("documentType");
  const documentStatus = isEmbedded ? null : searchParams.get("documentStatus");

  // Handle building change — no-op in embedded mode (host page owns the route).
  const handleBuildingChange = useCallback(
    (newBuildingId: string) => {
      if (isEmbedded) return;
      const newParams = new URLSearchParams(searchParams);
      newParams.set("building", newBuildingId);
      newParams.delete("buildingId");
      navigate(`/renovation/documents?${newParams.toString()}`);
    },
    [navigate, searchParams, isEmbedded],
  );

  // Filter management hook
  const {
    searchText,
    setSearchText,
    activeFilters,
    setActiveFilters,
    apiFilters,
    filterBarSections,
    filterBarChips,
    isRestoringFromURL,
    handleFiltersFromURL,
  } = useRenovationDocuments({
    initialFilters: {
      documentType: documentType || undefined,
      documentStatus: documentStatus || undefined,
      projectId: projectId ? Number(projectId) : undefined,
      projectPartId: projectPartId ? Number(projectPartId) : undefined,
    },
    currentBuildingId: buildingId || undefined,
    onBuildingChange: handleBuildingChange,
    navigate: isEmbedded ? undefined : navigate,
    lockedProjectId: props.lockedProjectId,
    lockedBuildingId: isEmbedded ? props.buildingId : undefined,
    lockedProjectPartId: props.lockedProjectPartId,
  });

  // Anchor for the documents table so "Bekijk" in the reconciliation panel can
  // apply a filter and scroll the matching invoices into view.
  const tableAnchorRef = useRef<HTMLDivElement>(null);
  const handleShowInTable = useCallback(
    (status: "missing" | "mismatch") => {
      setSelectedRows(new Set());
      setActiveFilters((prev) => ({ ...prev, odooStatus: [status] }));
      // Defer scroll until the filter-driven re-render settles.
      window.setTimeout(() => {
        tableAnchorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 50);
    },
    [setActiveFilters],
  );

  // Data fetching hook with page-based pagination
  const {
    documents,
    isLoading,
    totalCount,
    page,
    pageSize,
    setPage,
    fetchDocuments,
    removeDocuments,
    hasActiveFilters,
  } = useRenovationDocumentsData(buildingId || "", apiFilters);

  // Server-side stats hook
  const {
    total: statsTotal,
    filtered: statsFiltered,
    isLoading: isStatsLoading,
    refetch: refetchStats,
  } = useRenovationDocumentsStats(buildingId || undefined, apiFilters);

  // Voorzien budget uit de investeringslijnen, op dezelfde gebouwscope als de documenten.
  // Voedt de budgetregel onder de types ("gefactureerd / budget").
  const { totals: budgetTotals } = useInvestmentBudgetTotals(buildingId || undefined);

  // Import reconciliation totals (Odoo accounting vs YallyHub) for the KPI cards.
  // The "reeds geïmporteerd" bucket honours the active filters; with none it
  // shows the full total.
  const {
    data: reconciliation,
    isLoading: isReconciliationLoading,
    refetch: refetchReconciliation,
  } = useImportReconciliation(buildingId || undefined, apiFilters);

  // The "reeds geïmporteerd" KPI is driven from the SAME data as the
  // "Gefilterd resultaat → Facturen" breakdown (statsFiltered), so the two are
  // always pixel-identical and the card can never disagree or show €0. The
  // other two buckets ("te importeren", "zonder kostenplaats") come from the
  // reconciliation endpoint (Odoo-sourced). Falls back to the backend imported
  // value only while the stats are still loading.
  const reconciliationCardsData = useMemo<ImportReconciliation | null>(() => {
    if (!reconciliation && !statsFiltered) return null;
    const factuur = statsFiltered?.byType?.factuur?.total;
    const importedBucket = factuur
      ? { count: factuur.count, value: factuur.amount }
      : reconciliation?.imported ?? { count: 0, value: 0 };
    const leeg = { count: 0, value: 0 };
    return {
      imported: importedBucket,
      toImport: reconciliation?.toImport ?? leeg,
      withoutKostenplaats: reconciliation?.withoutKostenplaats ?? leeg,
      // De globale emmers komen ongefilterd van de backend en worden NIET vervangen
      // door de statscijfers: die volgen wél de filters. Daarop staat de rekensom.
      global: reconciliation?.global ?? {
        imported: importedBucket,
        toImport: reconciliation?.toImport ?? leeg,
        withoutKostenplaats: reconciliation?.withoutKostenplaats ?? leeg,
      },
      // Zolang de reconciliatie nog niet binnen is, niets beweren: pas na het
      // antwoord weten we of de Odoo-cijfers berekend konden worden.
      analyticsReady: reconciliation?.analyticsReady ?? true,
      hasScope: reconciliation?.hasScope ?? (!!buildingId || hasActiveFilters),
      scopedBuildingId: reconciliation?.scopedBuildingId ?? buildingId ?? null,
      ledgerAccounts: reconciliation?.ledgerAccounts ?? [],
      lastUpdated: reconciliation?.lastUpdated ?? null,
    };
  }, [reconciliation, statsFiltered, buildingId, hasActiveFilters]);

  /**
   * Aantal actieve filters buiten gebouw en project — voor de scope-banner, zodat die
   * kan zeggen dat er nóg filters aan staan (jaar, type, status, leverancier, ...).
   */
  const otherFilterCount = useMemo(
    () =>
      Object.entries(activeFilters).filter(
        ([key, waarden]) => key !== "project" && (waarden as string[])?.length > 0,
      ).length + (searchText.trim() ? 1 : 0),
    [activeFilters, searchText],
  );

  // Switch tabs and RESET all filters, so each tab starts with a clean slate
  // (the two tabs filter independently). Clears the building scope + every
  // active filter chip and the search term.
  const handleTabChange = useCallback(
    (next: "te-importeren" | "geimporteerd") => {
      if (next !== activeTab) {
        setSearchText("");
        setSelectedRows(new Set());
        setActiveFilters({
          type: [],
          status: [],
          project: [],
          projectPart: [],
          supplier: [],
          year: [],
        });
        // Clears ?building / ?buildingId and any other filter params from the URL.
        if (!isEmbedded) navigate("/renovation/documents");
      }
      setActiveTab(next);
      // Remember the choice so a page refresh stays on this tab.
      try {
        sessionStorage.setItem(ACTIVE_TAB_STORAGE_KEY, next);
      } catch {
        /* sessionStorage unavailable — ignore */
      }
    },
    [activeTab, isEmbedded, navigate, setActiveFilters, setSearchText],
  );

  // ONE global "Vernieuwen": rebuilds the analytics cache (one Odoo sweep) and
  // then refreshes ALL import sections at once — the per-building list, the
  // zonder-kostenplaats list and the KPIs — not just one of them.
  const handleRefreshAnalytics = useCallback(async () => {
    if (!session) return;
    setIsRefreshingAnalytics(true);
    try {
      await fetchWithAuth(
        `${API_URL}/renovation/refresh-analytics`,
        { method: "GET" },
        session,
      );
    } catch (error) {
      console.error("Error refreshing renovation analytics:", error);
      toast.error("Fout bij vernieuwen van de gegevens");
    } finally {
      setIsRefreshingAnalytics(false);
      // Tell the child lists to refetch the freshly-rebuilt cached data.
      setAnalyticsRefreshToken((t) => t + 1);
      refetchReconciliation();
    }
  }, [session, refetchReconciliation]);

  // Check if we have data
  const hasData = documents.length > 0;

  // Freshness of the daily Odoo payment-status sync — the most recent
  // "gecontroleerd" timestamp across the loaded documents (the cron stamps them
  // together, so the max is representative).
  const paymentStatusCheckedAt = useMemo<Date | null>(() => {
    const times = documents
      .map((d) => (d as { paymentStatusUpdatedAt?: string }).paymentStatusUpdatedAt)
      .filter((t): t is string => !!t)
      .map((t) => new Date(t).getTime())
      .filter((n) => !Number.isNaN(n));
    return times.length ? new Date(Math.max(...times)) : null;
  }, [documents]);

  const [groupBy] = useState<string>("none");

  // Fetch renovation invoices, projects and building when building is selected
  useEffect(() => {
    const fetchBuildingData = async () => {
      if (!buildingId || !session) return;

      setIsLoadingInvoices(true);
      try {
        // Fetch building data for the upload modal
        try {
          const buildingResponse = await fetchWithAuth(
            `${API_URL}/building/${buildingId}`,
            { method: "GET" },
            session,
          );

          if (buildingResponse.ok) {
            const building = await buildingResponse.json();
            setCurrentBuilding(building);
          } else {
            console.warn(`⚠️ Could not fetch building data for ${buildingId}`);
            setCurrentBuilding(null);
          }
        } catch (buildingError) {
          console.error("❌ Error fetching building:", buildingError);
          setCurrentBuilding(null);
        }

        // Fetch renovation invoices for this building
        try {
          const invoicesResponse = await fetchWithAuth(
            `${API_URL}/renovation/building/${buildingId}/available-invoices`,
            { method: "GET" },
            session,
          );

          if (invoicesResponse.ok) {
            const invoices = await invoicesResponse.json();
            setRenovationInvoices(invoices);
          } else if (invoicesResponse.status === 404) {
            console.warn(
              `⚠️ Invoice import endpoint not available yet (404).`,
            );
            setRenovationInvoices([]);
          } else {
            throw new Error(
              `Failed to fetch invoices: ${invoicesResponse.statusText}`,
            );
          }
        } catch (invoiceError) {
          console.error("❌ Error fetching renovation invoices:", invoiceError);
          setRenovationInvoices([]);
        }

        // Fetch available projects for this building
        try {
          const projectsResponse = await fetchWithAuth(
            `${API_URL}/renovation/project/building/${buildingId}`,
            { method: "GET" },
            session,
          );

          if (projectsResponse.ok) {
            const projects = await projectsResponse.json();
            setAvailableProjects(projects);
          } else if (projectsResponse.status === 404) {
            console.warn(`⚠️ No projects found for building ${buildingId}`);
            setAvailableProjects([]);
          } else {
            throw new Error(
              `Failed to fetch projects: ${projectsResponse.statusText}`,
            );
          }
        } catch (projectError) {
          console.error("❌ Error fetching projects:", projectError);
          setAvailableProjects([]);
          toast.error("Fout bij ophalen van projecten voor dit gebouw");
        }
      } catch (error) {
        console.error("❌ Error in fetchBuildingData:", error);
        setRenovationInvoices([]);
        setAvailableProjects([]);
        setCurrentBuilding(null);
      } finally {
        setIsLoadingInvoices(false);
      }
    };

    fetchBuildingData();
  }, [buildingId, session]);

  // Handle opening upload modal
  const handleOpenUploadModal = useCallback(() => {
    if (!buildingId) {
      toast.warn("Selecteer eerst een gebouw");
      return;
    }
    setIsDocumentUploadModalOpen(true);
  }, [buildingId]);

  // Handle document upload success
  const handleDocumentUploadSuccess = useCallback(() => {
    setIsDocumentUploadModalOpen(false);
    fetchDocuments();
  }, [fetchDocuments]);

  // Handle opening invoice import modal
  const handleOpenInvoiceImportModal = useCallback(() => {
    if (!buildingId) {
      toast.warn("Selecteer eerst een gebouw om facturen te importeren");
      return;
    }
    setIsInvoiceImportModalOpen(true);
  }, [buildingId]);

  // Handle status click - open status picker
  const handleStatusClick = useCallback(
    (document: RenovationDocument) => {
      setStatusPickerDocument(document);
      setIsStatusPickerOpen(true);
    },
    [],
  );

  // Handle status change confirmation
  const handleStatusConfirm = useCallback(
    async (newStatus: DocumentApprovalStatus) => {
      if (!statusPickerDocument) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/renovation/document/${statusPickerDocument.id}/status`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              status: newStatus,
              userName: "User",
            }),
          },
          session,
        );

        if (!response.ok) {
          throw new Error(`Failed to update status: ${response.statusText}`);
        }

        toast.success("Status succesvol bijgewerkt");
        setIsStatusPickerOpen(false);
        setStatusPickerDocument(null);
        fetchDocuments();
      } catch (error) {
        console.error("Error updating status:", error);
        toast.error("Fout bij het bijwerken van de status");
        throw error;
      }
    },
    [statusPickerDocument, session, fetchDocuments],
  );

  // Handle date click - open date picker
  const handleDateClick = useCallback((document: RenovationDocument) => {
    setDatePickerDocument(document);
    setIsDatePickerOpen(true);
  }, []);

  // Handle date change confirmation
  const handleDateConfirm = useCallback(
    async (newDate: string) => {
      if (!datePickerDocument) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/renovation/document/${datePickerDocument.id}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ documentDate: newDate }),
          },
          session,
        );

        if (!response.ok) {
          throw new Error(`Failed to update date: ${response.statusText}`);
        }

        toast.success("Documentdatum succesvol bijgewerkt");
        setIsDatePickerOpen(false);
        setDatePickerDocument(null);
        fetchDocuments();
      } catch (error) {
        console.error("Error updating date:", error);
        toast.error("Fout bij het bijwerken van de datum");
        throw error;
      }
    },
    [datePickerDocument, session, fetchDocuments],
  );

  // Handle delete click - open confirmation
  const handleDeleteClick = useCallback((document: RenovationDocument) => {
    setDeleteTargetDocument(document);
    setIsDeleteConfirmOpen(true);
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTargetDocument) return;

    const documentId = deleteTargetDocument.id;
    setDeletingDocumentIds((prev) => new Set(prev).add(documentId));
    setIsDeleteConfirmOpen(false);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/document/${documentId}`,
        { method: "DELETE" },
        session,
      );

      if (!response.ok) {
        throw new Error(`Failed to delete document: ${response.statusText}`);
      }

      toast.success(
        `Document ${deleteTargetDocument.documentNumber || documentId} succesvol verwijderd`,
      );
      setDeleteTargetDocument(null);
      removeDocuments([documentId]);
      fetchDocuments();
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Fout bij het verwijderen van het document");
      throw error;
    } finally {
      setDeletingDocumentIds((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
    }
  }, [deleteTargetDocument, session, fetchDocuments, removeDocuments]);

  // Handle delete cancel
  const handleDeleteCancel = useCallback(() => {
    setIsDeleteConfirmOpen(false);
    setDeleteTargetDocument(null);
  }, []);

  // Bulk delete the currently selected documents.
  const handleBulkDeleteConfirm = useCallback(async () => {
    const ids = Array.from(selectedRows);
    if (ids.length === 0) return;

    setIsBulkDeleting(true);
    setDeletingDocumentIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });

    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/documents`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids }),
        },
        session,
      );

      if (!response.ok) {
        const text = await response.text().catch(() => response.statusText);
        throw new Error(`${response.status}: ${text}`);
      }

      const result: {
        deleted: number[];
        failed: { id: number; reason: string }[];
      } = await response.json();

      if (result.deleted.length > 0) {
        toast.success(`${result.deleted.length} document(en) verwijderd`);
        // Drop the deleted rows immediately so they don't linger behind the
        // (now background) resync.
        removeDocuments(result.deleted);
      }
      if (result.failed.length > 0) {
        toast.error(
          `${result.failed.length} document(en) konden niet verwijderd worden`,
        );
      }

      setSelectedRows(new Set());
      setIsBulkDeleteOpen(false);
      fetchDocuments();
      refetchReconciliation();
      // Imported set changed → refetch te-importeren list/KPIs and Odoo marks.
      setAnalyticsRefreshToken((t) => t + 1);
    } catch (error) {
      console.error("Error bulk deleting documents:", error);
      toast.error("Fout bij het verwijderen van de geselecteerde documenten");
      throw error;
    } finally {
      setIsBulkDeleting(false);
      setDeletingDocumentIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, [selectedRows, session, fetchDocuments, removeDocuments, refetchReconciliation]);

  // Force an immediate Odoo payment-status sync (otherwise daily via cron).
  const handleSyncPaymentStatus = useCallback(async () => {
    if (!session || isSyncingPayments) return;
    setIsSyncingPayments(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/payment-status/refresh`,
        { method: "POST" },
        session,
      );
      if (!response.ok) {
        const text = await response.text().catch(() => response.statusText);
        throw new Error(`${response.status}: ${text}`);
      }
      const result: {
        total: number;
        synced: number;
        /** Facturen die door deze ronde op "Goedgekeurd" kwamen (betaald in Odoo). */
        approved?: number;
      } = await response.json();
      if (result.synced > 0) {
        toast.success(`${result.synced} betaalstatussen bijgewerkt uit Odoo`);
        if (result.approved) {
          toast.info(
            result.approved === 1
              ? "1 betaalde factuur op Goedgekeurd gezet"
              : `${result.approved} betaalde facturen op Goedgekeurd gezet`,
          );
        }
        fetchDocuments();
        // De statusopsplitsing ("Ontvangen (11)") verandert mee zodra er
        // facturen goedgekeurd werden, dus die telling opnieuw ophalen.
        refetchStats();
      } else if (result.total > 0) {
        toast.warn(
          "Geen betaalstatussen ontvangen uit Odoo — probeer later opnieuw",
        );
      } else {
        toast.info("Geen geïmporteerde facturen om te synchroniseren");
      }
    } catch (error) {
      console.error("Error syncing payment statuses:", error);
      toast.error("Betaalstatus synchroniseren mislukt");
    } finally {
      setIsSyncingPayments(false);
    }
  }, [session, isSyncingPayments, fetchDocuments, refetchStats]);

  // Handle invoice import success
  const handleInvoiceImportSuccess = useCallback(() => {
    fetchDocuments();
    refetchReconciliation();
    // The backend already removed the imported invoice from the cached metrics,
    // so bumping the token makes the "te importeren" list/badge refetch and the
    // invoice drops off immediately — no Odoo rebuild needed.
    setAnalyticsRefreshToken((t) => t + 1);
    if (buildingId && session) {
      fetchWithAuth(
        `${API_URL}/renovation/building/${buildingId}/available-invoices`,
        { method: "GET" },
        session,
      )
        .then(async (response) => {
          if (response.ok) {
            const invoices = await response.json();
            setRenovationInvoices(invoices);
          } else if (response.status === 404) {
            setRenovationInvoices([]);
          }
        })
        .catch((error) => {
          console.error("❌ Error refreshing invoices after import:", error);
          setRenovationInvoices([]);
        });
    }
  }, [buildingId, session, fetchDocuments, refetchReconciliation]);

  // Render helper for table row
  const renderRow = useCallback(
    (document: any, rowContext?: any) => {
      return createDocumentCells({
        document,
        rowContext,
        handlers: {
          onDocumentClick: (doc: any) => {
            if (doc.sharePointFileUrl) {
              window.open(doc.sharePointFileUrl, "_blank");
            } else if (doc.fileUrl) {
              window.open(doc.fileUrl, "_blank");
            } else {
              toast.info("Geen bestand beschikbaar voor dit document");
            }
          },
          onProjectClick: (projectId: number) => {
            if (!projectId) {
              console.error("Invalid project ID:", projectId);
              return;
            }
            navigate(`/renovation/${projectId}`);
          },
          onStatusClick: handleStatusClick,
          onDateClick: handleDateClick,
          onDeleteClick: handleDeleteClick,
          onPurchaseOrderClick: setPurchaseOrderDocument,
          onQuoteClick: setQuoteDocument,
          onRenoTicketClick: setRenoTicketDocument,
        },
        users,
        deletingDocumentIds,
      });
    },
    [navigate, users, handleStatusClick, handleDateClick, handleDeleteClick, deletingDocumentIds],
  );

  // Pagination control rendered inside FilterBar rightSlot
  const paginationControl = (
    <TablePagination
      page={page}
      pageSize={pageSize}
      totalCount={totalCount}
      onPageChange={setPage}
      isLoading={isLoading}
    />
  );

  // Import action buttons — shown inside the "Te importeren facturen" tab.
  // Hidden in embedded mode (host page owns its own chrome).
  const importActionButtons = !isEmbedded && (
    <div className="flex flex-col items-end gap-2">
      <ButtonNew
        variant="secondaryMoss"
        icon={<Plus />}
        label={
          isLoadingInvoices
            ? "Facturen laden..."
            : renovationInvoices.length > 0
              ? `${renovationInvoices.length} facturen importeren`
              : "Importeer facturen"
        }
        onClick={handleOpenInvoiceImportModal}
        isDisabled={!buildingId || isLoadingInvoices}
      />
      {!buildingId && (
        <div className="bg-brick-secondary-fine border-brick-primary/20 rounded-lg border px-3 py-1 text-sm text-neutral-600">
          Selecteer een gebouw om facturen te importeren
        </div>
      )}
      {buildingId && isLoadingInvoices && (
        <div className="flex animate-pulse items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-sm text-neutral-600">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
          Controleren beschikbare facturen...
        </div>
      )}
      {buildingId &&
        !isLoadingInvoices &&
        renovationInvoices.length > 0 && (
          <div className="bg-moss-primary/5 border-moss-primary/20 rounded-lg border px-3 py-1 text-sm text-neutral-600">
            <strong>{renovationInvoices.length}</strong> facturen beschikbaar
            om te importeren
          </div>
        )}
      {buildingId &&
        !isLoadingInvoices &&
        renovationInvoices.length === 0 && (
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-1 text-sm text-neutral-600">
            Geen facturen beschikbaar om te importeren
          </div>
        )}
    </div>
  );

  // Upload action button — shown inside the "Geïmporteerde facturen" tab,
  // directly above the financiële documenten table. Hidden in embedded mode.
  const uploadActionButton = !isEmbedded && (
    <ButtonNew
      variant="primaryMoss"
      icon={<Plus />}
      label="Document uploaden"
      onClick={handleOpenUploadModal}
    />
  );

  const documentsTabContent = (
    <>
      {/* Loading State — only the full block on the FIRST load (no data yet).
          On a refetch with rows already shown we keep the table visible (rows
          are removed optimistically on delete) so it never duplicates a spinner
          on top of stale rows. */}
      {isLoading && !hasData && (
        <div className="px-6 pt-4 pb-6">
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
            <div className="mb-4 flex justify-center">
              <div className="border-moss-primary h-12 w-12 animate-spin rounded-full border-b-2"></div>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              Documenten aan het laden...
            </h3>
            <p className="text-gray-600">
              Even geduld terwijl we de renovatiedocumenten ophalen.
            </p>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {!isLoading && !hasData && (
        <div className="px-6 pt-4 pb-6">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
            <FileText
              size={48}
              weight="duotone"
              className="mx-auto mb-4 text-gray-400"
            />
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              Geen documenten gevonden
            </h3>
            <p className="mb-4 text-gray-600">
              {buildingId
                ? `Er zijn geen renovatiedocumenten beschikbaar voor gebouw ${buildingId}.`
                : "Er zijn geen renovatiedocumenten beschikbaar."}
            </p>
          </div>
        </div>
      )}

      {/* Bulk action bar — appears once one or more rows are selected. */}
      {hasData && selectedRows.size > 0 && (
        <div className="mx-6 mb-3 flex items-center justify-between gap-3 rounded-lg border border-brick-primary/30 bg-brick-secondary-fine px-4 py-2.5">
          <span className="text-sm font-medium text-neutral-primary">
            {selectedRows.size} document(en) geselecteerd
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedRows(new Set())}
            >
              Selectie wissen
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => setIsBulkDeleteOpen(true)}
            >
              <Trash size={14} />
              Verwijder selectie
            </Button>
          </div>
        </div>
      )}

      {/* Betaalstatus-versheid + handmatige sync — daily Odoo cron + button. */}
      {hasData && (
        <div className="flex items-center justify-between gap-2 px-6 pb-1">
          <span className="text-xs text-neutral-secondary-mid">
            {paymentStatusCheckedAt
              ? `Betaalstatus laatst gecontroleerd in Odoo op ${paymentStatusCheckedAt.toLocaleDateString(
                  "nl-BE",
                  { day: "2-digit", month: "2-digit", year: "numeric" },
                )} · dagelijks automatisch bijgewerkt · betaalde facturen komen op Goedgekeurd`
              : "Betaalstatus nog niet gesynchroniseerd met Odoo"}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncPaymentStatus}
            disabled={isSyncingPayments}
          >
            <ArrowClockwise
              size={14}
              className={isSyncingPayments ? "animate-spin" : ""}
            />
            {isSyncingPayments ? "Bezig…" : "Vernieuwen betaalstatus"}
          </Button>
        </div>
      )}

      {/* Legende bij de kolom Kostenplaatsen. De kostenplaats komt uit de
          boekhouding zodra Odoo er een heeft; de kleur zegt of die overeenkomt
          met wat op het renovatieproject voorzien is. */}
      {hasData && (
        <div className="flex flex-wrap items-center gap-2 px-6 pb-2 text-xs text-neutral-secondary-mid">
          <span>Kostenplaatsen:</span>
          <span className="rounded border border-green-200 bg-green-50 px-2 py-0.5 text-green-700">
            komt overeen met het renovatieproject
          </span>
          <span className="rounded border border-red-200 bg-red-50 px-2 py-0.5 text-red-700">
            wijkt af van de voorziene kostenplaatsen
          </span>
          <span className="rounded border border-neutral-200 bg-neutral-50 px-2 py-0.5">
            niets om mee te vergelijken
          </span>
        </div>
      )}

      {/* Table - only show if we have data */}
      {hasData && (
        <div className="mb-4">
          <UniversalTable
            columnsConfig={{
              columns: DOCUMENTS_TABLE_CONFIG.columns.map((col) => ({
                ...col,
                defaultWeight: col.weight,
              })),
              // -v2 sinds de offertekolom erbij kwam: de oude sleutel bewaarde
              // breedtes voor een kolommenreeks die niet meer bestaat.
              localStorageKey: "renovation-documents-columns-v2",
            }}
            rows={documents}
            groupBy={groupBy}
            title=""
            renderRow={renderRow}
            selectedRows={selectedRows}
            onSelectionChange={setSelectedRows}
          />
        </div>
      )}
    </>
  );

  return (
    <>
      <div className="bg-brick-secondary-fine flex flex-col gap-0">
        {/* Page header — hidden in embedded mode (host page provides its own). */}
        {!isEmbedded && (
          <header className="px-6 pt-6 pb-4">
            <div className="flex items-center gap-3">
              <FileText
                size={32}
                weight="duotone"
                className="text-moss-primary"
              />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Renovatie Documenten
                </h1>
                {/* De actieve filter staat in DocumentsScopeBanner onder de tabs, niet
                    meer als grijze regel hier: die werd over het hoofd gezien en dan las
                    je een gefilterd bedrag als het totaal. */}
              </div>
            </div>
          </header>
        )}

        {/* In embedded mode the host page owns chrome — just render the docs
            table without tabs. The reconciliation KPI cards live on the tabs,
            so embedded mode keeps the side-by-side document stats. */}
        {isEmbedded ? (
          <>
            <RenovationDocumentsStats
              total={statsTotal}
              filtered={statsFiltered}
              hasActiveFilters={hasActiveFilters}
              isLoading={isStatsLoading}
            />
            {documentsTabContent}
          </>
        ) : (
          <Tabs
            value={activeTab}
            onValueChange={(v) =>
              handleTabChange(v as "te-importeren" | "geimporteerd")
            }
            className="w-full"
          >
            {/* Tabs met een expliciete "Je bekijkt:"-aanduiding ervoor. Het verwarren
                van de twee tabs was een terugkerende fout: beide heten "facturen" en het
                verschil zat enkel in de kleur van de actieve tab. */}
            <div className="px-6 pt-2 pb-2">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-neutral-secondary-mid text-xs font-semibold tracking-wider uppercase">
                  Je bekijkt
                </span>
                <TabsList className="bg-muted h-11 gap-1 p-1">
                  <TabsTrigger
                    value="te-importeren"
                    className="text-neutral-secondary-mid data-[state=active]:bg-brick-primary flex h-9 items-center gap-2 px-5 text-sm font-medium data-[state=active]:text-white data-[state=active]:shadow-none"
                  >
                    <DownloadSimple size={14} weight="duotone" />
                    Nog te importeren
                    {pendingInvoiceCount > 0 && (
                      <span className="bg-neutral-200 text-neutral-secondary-mid data-[state=active]:bg-white/20 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold data-[state=active]:text-white">
                        {pendingInvoiceCount}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger
                    value="geimporteerd"
                    className="text-neutral-secondary-mid data-[state=active]:bg-moss-primary flex h-9 items-center gap-2 px-5 text-sm font-medium data-[state=active]:text-white data-[state=active]:shadow-none"
                  >
                    <CheckCircle size={14} weight="duotone" />
                    Al geïmporteerd
                    {statsTotal && statsTotal.documentCount > 0 && (
                      <span className="bg-neutral-200 text-neutral-secondary-mid data-[state=active]:bg-white/20 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold data-[state=active]:text-white">
                        {statsTotal.documentCount}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>
                <span
                  className={`rounded-md border px-2 py-1 text-xs font-medium ${
                    activeTab === "te-importeren"
                      ? "border-brick-primary/30 bg-brick-primary/10 text-brick-primary"
                      : "border-moss-primary/30 bg-moss-primary/10 text-moss-primary"
                  }`}
                >
                  {activeTab === "te-importeren"
                    ? "Facturen die nog in YallyHub moeten komen"
                    : "Facturen die al in YallyHub staan"}
                </span>
              </div>
            </div>

            {/* Onmiskenbaar wat er gefilterd staat — op beide tabs. */}
            <DocumentsScopeBanner
              buildingId={buildingId || undefined}
              projectId={projectId || undefined}
              otherFilterCount={otherFilterCount}
              onClearBuilding={
                buildingId && !isEmbedded
                  ? () => navigate("/renovation/documents")
                  : undefined
              }
            />

            <TabsContent value="te-importeren" className="mt-0">
              <div className="px-6 pt-4 pb-2 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-neutral-primary">
                    Importeren vanuit ontvangen facturen
                  </h2>
                  <p className="text-sm text-neutral-secondary-mid">
                    Klik op een gebouw om dat te selecteren en gebruik daarna
                    de knop hiernaast om de facturen te importeren.
                  </p>
                </div>
                {importActionButtons}
              </div>
              <ImportReconciliationCards
                data={reconciliationCardsData}
                isLoading={isReconciliationLoading || isStatsLoading}
                buildingId={buildingId || undefined}
              />
              <LedgerAccountBreakdown
                buildingId={buildingId || undefined}
                year={apiFilters.year}
              />
              <BuildingsWithPendingInvoices
                activeBuildingId={buildingId || undefined}
                onCountChange={setPendingInvoiceCount}
                refreshToken={analyticsRefreshToken}
                isRefreshing={isRefreshingAnalytics}
                onRefresh={handleRefreshAnalytics}
              />
              <UnassignedInvoices
                refreshToken={analyticsRefreshToken}
                isRefreshing={isRefreshingAnalytics}
                onRefresh={handleRefreshAnalytics}
              />
            </TabsContent>

            <TabsContent value="geimporteerd" className="mt-0">
              <div className="px-6 pt-4 pb-2 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-neutral-primary">
                    Financiële documenten
                  </h2>
                  <p className="text-sm text-neutral-secondary-mid">
                    Beheer geïmporteerde facturen, offertes, bestelbonnen en
                    andere financiële documenten.
                  </p>
                </div>
                {uploadActionButton}
              </div>
              <ImportReconciliationCards
                data={reconciliationCardsData}
                isLoading={isReconciliationLoading || isStatsLoading}
                buildingId={buildingId || undefined}
              />
              <LedgerAccountBreakdown
                buildingId={buildingId || undefined}
                year={apiFilters.year}
              />
              {buildingId && (
                <ReconciliationDebug
                  buildingId={buildingId}
                  onShowInTable={handleShowInTable}
                />
              )}
              <RenovationDocumentsStats
                total={statsTotal}
                filtered={statsFiltered}
                hasActiveFilters={hasActiveFilters}
                isLoading={isStatsLoading}
                showTotal={false}
                buildingId={buildingId || undefined}
                netBudgetExclVat={budgetTotals?.netBudgetExclVat ?? null}
              />
              <div ref={tableAnchorRef}>{documentsTabContent}</div>
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* FilterBar — shown on BOTH tabs (and in embedded mode). On the
          "Te importeren" tab clicking a building row adds its "Gebouw" chip
          here (removable); the document pagination only makes sense on the
          "Geïmporteerde facturen" view, so it's the only place with a rightSlot. */}
      <FilterBar
        searchValue={searchText}
        onSearchChange={(value) => {
          if (!isRestoringFromURL) {
            setSearchText(value);
          }
        }}
        searchPlaceholder="Zoek documenten op nummer, leverancier..."
        filterChips={filterBarChips}
        filterSections={filterBarSections}
        syncWithURL={!isEmbedded}
        urlParamPrefix=""
        onFiltersFromURL={isEmbedded ? undefined : handleFiltersFromURL}
        showWhenEmpty={true}
        showSpacer={false}
        rightSlot={
          isEmbedded || activeTab === "geimporteerd"
            ? paginationControl
            : undefined
        }
      />

      {/* Invoice Import Modal */}
      {buildingId && (
        <RenovationInvoiceImportModal
          isOpen={isInvoiceImportModalOpen}
          onClose={() => setIsInvoiceImportModalOpen(false)}
          buildingId={buildingId}
          onSuccess={handleInvoiceImportSuccess}
          availableProjects={availableProjects}
          renovationInvoices={renovationInvoices}
        />
      )}

      {/* Document Upload Modal */}
      <DocumentUploadModal
        isOpen={isDocumentUploadModalOpen}
        onClose={() => setIsDocumentUploadModalOpen(false)}
        onSuccess={handleDocumentUploadSuccess}
        building={currentBuilding || undefined}
        units={currentBuilding?.units}
      />

      {/* Status Picker Modal */}
      <UniversalPickerModal
        isOpen={isStatusPickerOpen}
        onClose={() => {
          setIsStatusPickerOpen(false);
          setStatusPickerDocument(null);
        }}
        mode="custom-tags"
        title={`Wijzig status voor ${statusPickerDocument?.documentNumber || "document"}`}
        config={{
          defaultValue: statusPickerDocument?.status,
          customOptions: Object.values(DocumentApprovalStatus).map(
            (status) => ({
              value: status,
              label: status,
            }),
          ),
        }}
        onConfirm={handleStatusConfirm}
      />

      {/* Date Picker Modal */}
      <UniversalPickerModal
        isOpen={isDatePickerOpen}
        onClose={() => {
          setIsDatePickerOpen(false);
          setDatePickerDocument(null);
        }}
        mode="document-date"
        title={`Wijzig datum voor ${datePickerDocument?.documentNumber || "document"}`}
        config={{
          defaultValue: datePickerDocument?.documentDate
            ? format(new Date(datePickerDocument.documentDate), "yyyy-MM-dd")
            : undefined,
        }}
        onConfirm={handleDateConfirm}
      />

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteConfirmOpen}
        onOpenChange={(open) => !open && handleDeleteCancel()}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <Warning size={24} className="text-red-600" weight="fill" />
              </div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Document verwijderen
              </DialogTitle>
            </div>
          </DialogHeader>
          <DialogDescription className="text-gray-600">
            Weet u zeker dat u het document{" "}
            <strong className="text-gray-900">
              {deleteTargetDocument?.documentNumber || "dit document"}
            </strong>{" "}
            wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt en
            verwijdert ook alle bijbehorende documentregels.
          </DialogDescription>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleDeleteCancel}>
              Annuleren
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Modal */}
      <Dialog
        open={isBulkDeleteOpen}
        onOpenChange={(open) => !open && !isBulkDeleting && setIsBulkDeleteOpen(false)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <Warning size={24} className="text-red-600" weight="fill" />
              </div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Documenten verwijderen
              </DialogTitle>
            </div>
          </DialogHeader>
          <DialogDescription className="text-gray-600">
            Weet u zeker dat u{" "}
            <strong className="text-gray-900">
              {selectedRows.size} document(en)
            </strong>{" "}
            wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt en
            verwijdert ook alle bijbehorende documentregels.
          </DialogDescription>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsBulkDeleteOpen(false)}
              disabled={isBulkDeleting}
            >
              Annuleren
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDeleteConfirm}
              disabled={isBulkDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isBulkDeleting
                ? "Bezig met verwijderen..."
                : `Verwijder ${selectedRows.size} document(en)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bestelbon aan een factuur koppelen (een bestelbon kan in schijven
          gefactureerd worden, dus meerdere facturen per bestelbon). */}
      <LinkParentDocumentModal
        kind={BESTELBON_KOPPELING}
        document={
          purchaseOrderDocument && {
            id: purchaseOrderDocument.id,
            documentNumber: purchaseOrderDocument.documentNumber,
            parentId: purchaseOrderDocument.purchaseOrderId,
            parentNumber: purchaseOrderDocument.purchaseOrderNumber,
          }
        }
        onClose={() => setPurchaseOrderDocument(null)}
        onLinked={fetchDocuments}
      />

      {/* Offerte aan een factuur koppelen. Een offerte draagt geen factuurnummer,
          dus er valt niets automatisch te matchen — vandaar deze kiezer. Daarna
          toont de offerte hoeveel van haar bedrag al gefactureerd is. */}
      <LinkParentDocumentModal
        kind={OFFERTE_KOPPELING}
        document={
          quoteDocument && {
            id: quoteDocument.id,
            documentNumber: quoteDocument.documentNumber,
            parentId: quoteDocument.quoteId,
            parentNumber: quoteDocument.quoteNumber,
          }
        }
        onClose={() => setQuoteDocument(null)}
        onLinked={fetchDocuments}
      />

      {/* Renovatieticket koppelen vanuit de documentenlijst — kon eerder alleen op het
          ticket zelf, terwijl je hier de factuur voor je hebt. */}
      <LinkRenoTicketModal
        document={renoTicketDocument}
        onClose={() => setRenoTicketDocument(null)}
        onLinked={fetchDocuments}
      />
    </>
  );
};

export const RenovationDocumentsList = memo(RenovationDocumentsListComponent);
